import { useContext, useEffect, useState } from "react";
import { Switch, Route, useHistory } from "react-router-dom";

import { LoanFormContext, FormState } from "../LoanForm";
import TypeOfPropertyForm, { TypeOfProperty } from "../TypeOfProperty";
import PropertyUsage, { TypeOfUsageProperty } from "../PropertyUsage";
import ServedMilitary from "../ServedMilitary";
import CreditHistory from "../CreditHistory";
import CreditScore from "../CreditScore";
import Dob from "../Dob";
import Bankruptcy from "../Bankruptcy";
import Foreclosure from "../Foreclosure";
import Address from "../Address";
import NameDetails from "../NameDetails";
import EmailDetails from "../Email";
import ContactNumber from "../ContactNumber";
import WhyRefinance from "../WhyRefinance";
import PropertyZip from "../PropertyZip";
import PropertyEstimate from "../PropertyEstimate";
import HaveSecondMortage from "../HaveSecondMortage";
import PropertyLocation from "../PropertyLocation";
import PropertyRemainingBalance from "../PropertyRemainingBalance";
import SecondMortageRemaining from "../SecondMortageRemaining";
import HaveVALoan from "../HaveVALoan";

import { CircularProgressbar } from "react-circular-progressbar";

const RefinanceForm: React.FunctionComponent<{ onSubmit?: () => void }> =
  (props: { onSubmit?: () => void }) => {
    const history = useHistory();
    const { setFormState, ...loanFormContext } = useContext(LoanFormContext);
    const [percent, setPercent] = useState<number>(0);

    useEffect(() => {
      setFormState((state: FormState) => ({
        ...state,
        steps: {
          current: 1,
          total: 18,
        },
      }));
    }, []);

    useEffect(() => {
      const l = history.listen((location, action) => {
        if (action === "POP") {
          setFormState((state: FormState) => {
            return {
              ...state,
              steps: {
                current: (state.steps ? state.steps.current : 0) - 1,
                total: state.steps ? state.steps.total : 0,
              },
            };
          });
        }
      });

      return () => {
        l();
      };
    }, []);

    useEffect(() => {
      const currentStep = loanFormContext.steps
        ? loanFormContext.steps?.current
        : 1;
      const totalSteps = loanFormContext.steps
        ? loanFormContext.steps?.total
        : 1;
      setPercent(Math.ceil((currentStep / totalSteps) * 100));
      console.log(currentStep, totalSteps);
    }, [loanFormContext.steps]);

    return (
      <div>
        <div className="progress-bar-wrapper">
          <CircularProgressbar value={percent} text={`${percent}%`} />
        </div>
        <Switch>
          <Route exact path="/refinance/type-of-property">
            <TypeOfPropertyForm
              onSubmit={(value: TypeOfProperty) => {
                setFormState((state: FormState) => {
                  return {
                    // ...state,
                    steps: {
                      total: state.steps ? state.steps.total : 0,
                      current: (state.steps ? state.steps.current : 0) + 1,
                    },
                    loading: false,
                    formStarted: true,
                    setFormState: state.setFormState,
                    typeOfProperty: { value },
                  };
                });
                history.push("/refinance/property-usage");
              }}
            />
          </Route>

          <Route path="/refinance/property-usage">
            <PropertyUsage
              onSubmit={(value: TypeOfUsageProperty) => {
                setFormState((state: FormState) => {
                  return {
                    ...state,
                    steps: {
                      total: state.steps ? state.steps.total : 0,
                      current: (state.steps ? state.steps.current : 0) + 1,
                    },
                    propertyUsage: { value },
                  };
                });
                history.push("/refinance/why-refinance");
              }}
            />
          </Route>

          <Route path="/refinance/why-refinance">
            <WhyRefinance
              onSubmit={(value) => {
                setFormState((state: FormState) => {
                  return {
                    ...state,
                    steps: {
                      total: state.steps ? state.steps.total : 0,
                      current: (state.steps ? state.steps.current : 0) + 1,
                    },
                    whyRefinance: { value },
                  };
                });
                history.push("/refinance/property-zip");
              }}
            />
          </Route>

          <Route path="/refinance/property-zip">
            <PropertyZip
              onSubmit={(value: string) => {
                setFormState((state: FormState) => {
                  return {
                    ...state,
                    steps: {
                      total: state.steps ? state.steps.total : 0,
                      current: (state.steps ? state.steps.current : 0) + 1,
                    },
                    propertyZip: { value },
                  };
                });
                history.push("/refinance/property-estimate");
              }}
            />
          </Route>

          <Route path="/refinance/property-estimate">
            <PropertyEstimate
              onSubmit={(value) => {
                setFormState((state: FormState) => {
                  return {
                    ...state,
                    steps: {
                      total: state.steps ? state.steps.total : 0,
                      current: (state.steps ? state.steps.current : 0) + 1,
                    },
                    propertyEstimate: {
                      value: { min: value[0], max: value[1] },
                    },
                  };
                });
                history.push("/refinance/property-remaining-balance");
              }}
            />
          </Route>

          <Route path="/refinance/property-remaining-balance">
            <PropertyRemainingBalance
              onSubmit={(value) => {
                setFormState((state: FormState) => {
                  return {
                    ...state,
                    steps: {
                      total: state.steps ? state.steps.total : 0,
                      current: (state.steps ? state.steps.current : 0) + 1,
                    },
                    propertyRemainingBalance: {
                      value: { min: value[0], max: value[1] },
                    },
                  };
                });
                history.push("/refinance/have-second-mortage");
              }}
            />
          </Route>

          <Route path="/refinance/have-second-mortage">
            <HaveSecondMortage
              onSubmit={(value) => {
                setFormState((state: FormState) => {
                  return {
                    ...state,
                    steps: {
                      total: state.steps ? state.steps.total : 0,
                      current: (state.steps ? state.steps.current : 0) + 1,
                    },
                    secondMortgage: {
                      value,
                    },
                  };
                });
                if (value === "yes") {
                  history.push("/refinance/remaining-second-mortage");
                } else {
                  history.push("/refinance/credit-score");
                }
              }}
            />
          </Route>

          <Route path="/refinance/remaining-second-mortage">
            <SecondMortageRemaining
              onSubmit={(value) => {
                setFormState((state: FormState) => {
                  return {
                    ...state,
                    steps: {
                      total: state.steps ? state.steps.total : 0,
                      current: (state.steps ? state.steps.current : 0) + 1,
                    },
                    secondMortageRemaining: {
                      value: { min: value[0], max: value[1] },
                    },
                  };
                });
                history.push("/refinance/credit-score");
              }}
            />
          </Route>

          <Route exact path="/refinance/credit-score">
            <CreditScore
              onSubmit={(value) => {
                setFormState((state: FormState) => ({
                  ...state,
                  steps: {
                    total: state.steps ? state.steps.total : 0,
                    current: (state.steps ? state.steps.current : 0) + 1,
                  },
                  creditScore: {
                    value,
                  },
                }));
                history.push("/refinance/dob");
              }}
            />
          </Route>

          <Route exact path="/refinance/dob">
            <Dob
              onSubmit={(value) => {
                setFormState((state: FormState) => ({
                  ...state,
                  steps: {
                    total: state.steps ? state.steps.total : 0,
                    current: (state.steps ? state.steps.current : 0) + 1,
                  },
                  dob: {
                    value,
                  },
                }));
                history.push("/refinance/served-military");
              }}
            />
          </Route>

          <Route exact path="/refinance/served-military">
            <ServedMilitary
              onSubmit={(value) => {
                setFormState((state: FormState) => ({
                  ...state,
                  steps: {
                    total: state.steps ? state.steps.total : 0,
                    current: (state.steps ? state.steps.current : 0) + 1,
                  },
                  servedMilitary: {
                    value,
                  },
                }));
                history.push("/refinance/have-va-loan");
              }}
            />
          </Route>

          <Route exact path="/refinance/have-va-loan">
            <HaveVALoan
              onSubmit={(value) => {
                setFormState((state: FormState) => ({
                  ...state,
                  haveVALoan: value,
                  steps: {
                    total: state.steps ? state.steps.total : 0,
                    current: (state.steps ? state.steps.current : 0) + 1,
                  },
                }));
                history.push("/refinance/credit-history");
              }}
            />
          </Route>

          <Route exact path="/refinance/credit-history">
            <CreditHistory
              onSubmit={(value) => {
                setFormState((state: FormState) => ({
                  ...state,
                  steps: {
                    total: state.steps ? state.steps.total : 0,
                    current: (state.steps ? state.steps.current : 0) + 1,
                  },
                  creditHistory: {
                    type: value,
                  },
                }));
                if (value === "both" || value === "bankruptcy") {
                  history.push("/refinance/bankrupt-history");
                } else if (value === "foreclosure") {
                  history.push("/refinance/foreclosure-history");
                } else {
                  history.push("/refinance/address");
                }
              }}
            />
          </Route>

          <Route exact path="/refinance/bankrupt-history">
            <Bankruptcy
              onSubmit={(value) => {
                setFormState((state: FormState) => ({
                  ...state,
                  // steps: {
                  //   total: state.steps ? state.steps.total : 0,
                  //   current: (state.steps ? state.steps.current : 0) + 1,
                  // },
                  creditHistory: {
                    type: state.creditHistory
                      ? state.creditHistory.type
                      : "bankruptcy",
                    bankruptcy: { yearsAgo: value },
                  },
                }));
                if (
                  loanFormContext.creditHistory?.type === "foreclosure" ||
                  loanFormContext.creditHistory?.type === "both"
                ) {
                  history.push("/refinance/foreclosure-history");
                } else {
                  history.push("/refinance/address");
                }
              }}
            />
          </Route>

          <Route exact path="/refinance/foreclosure-history">
            <Foreclosure
              onSubmit={(value) => {
                setFormState((state: FormState) => ({
                  ...state,
                  // steps: {
                  //   total: state.steps ? state.steps.total : 0,
                  //   current: (state.steps ? state.steps.current : 0) + 1,
                  // },
                  creditHistory: {
                    type: state.creditHistory
                      ? state.creditHistory.type
                      : "foreclosure",
                    foreclosure: { yearsAgo: value },
                  },
                }));
                history.push("/refinance/address");
              }}
            />
          </Route>

          <Route exact path="/refinance/address">
            <Address
              onSubmit={(value) => {
                setFormState((state: FormState) => ({
                  ...state,
                  address: value,
                  steps: {
                    total: state.steps ? state.steps.total : 0,
                    current: (state.steps ? state.steps.current : 0) + 1,
                  },
                }));
                history.push("/refinance/name-details");
              }}
            />
          </Route>

          <Route exact path="/refinance/name-details">
            <NameDetails
              onSubmit={(value) => {
                setFormState((state: FormState) => ({
                  ...state,
                  name: value,
                  steps: {
                    total: state.steps ? state.steps.total : 0,
                    current: (state.steps ? state.steps.current : 0) + 1,
                  },
                }));
                history.push("/refinance/email");
              }}
            />
          </Route>

          <Route exact path="/refinance/email">
            <EmailDetails
              onSubmit={(value) => {
                setFormState((state: FormState) => ({
                  ...state,
                  email: value,
                  steps: {
                    total: state.steps ? state.steps.total : 0,
                    current: (state.steps ? state.steps.current : 0) + 1,
                  },
                }));
                history.push("/refinance/contact-number");
              }}
            />
          </Route>

          <Route exact path="/refinance/contact-number">
            <ContactNumber
              onSubmit={(value) => {
                setFormState((state: FormState) => ({
                  ...state,
                  number: value,
                  steps: {
                    total: state.steps ? state.steps.total : 0,
                    current:
                      (state.steps && percent < 100 ? state.steps.current : 0) +
                      1,
                  },
                }));

                if (props.onSubmit) props.onSubmit();

                // history.push("/refinance/employment-status");
              }}
            />
          </Route>
        </Switch>
      </div>
    );
  };

export default RefinanceForm;
